import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import { COMPANY } from "../lib/company";

// Written in plain language on purpose. A privacy notice that only a lawyer can
// read fails the GDPR's own transparency requirement, and a beach club owner
// deciding whether to trust us will read this before they read anything else.
//
// English only for now: the outreach email carries the Article 14 disclosure in
// the owner's own language, which is the part the law actually times.
export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy and your data | sunbedly" },
      {
        name: "description",
        content:
          "What data sunbedly holds about beach clubs and visitors, where it came from, why we hold it, and how to have it removed.",
      },
    ],
  }),
  component: PrivacyPage,
});

const UPDATED = "27 July 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-[#1C2430]/10 py-8">
      <h2 className="li-serif text-2xl text-[#1C2430]">{title}</h2>
      <div className="mt-3 max-w-[68ch] space-y-3 text-[15px] leading-relaxed text-[#5B6472]">
        {children}
      </div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <Nav />
      <main className="mx-auto max-w-6xl px-5 pb-24">
        <div className="pt-12">
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#2C4FD6]">Privacy</p>
          <h1 className="li-serif mt-3 max-w-[20ch] text-4xl leading-[1.05] text-[#1C2430] md:text-6xl">
            What we hold, and how to make us stop
          </h1>
          <p className="li-mono mt-4 text-[11px] text-[#5B6472]">Last updated {UPDATED}</p>
        </div>

        {/* Said out loud rather than left for the reader to work out. A machine
            translation of a privacy notice reads fine and can be subtly wrong
            about what we do with someone's data, which is worse than English. */}
        <p className="li-mono mt-6 rounded-lg border border-[#1C2430]/15 bg-[#E7E2D6] px-4 py-3 text-[12px] leading-relaxed text-[#1C2430]">
          This notice is written in English only. If anything here is unclear in
          your language, email us and we will answer in Dutch, Italian, German,
          French or Spanish.
        </p>

        {/* The GDPR wants the controller identifiable, not repeated. The full
            registration block lives on /terms and this names the entity once
            and links there, so the two pages can never disagree. */}
        <Section title="Who is responsible">
          <p>
            sunbedly is a trade name of {COMPANY.legalName}, registered in the Netherlands under
            chamber of commerce number {COMPANY.kvk}. The full registration details are
            on our{" "}
            <Link to="/terms" className="text-[#2C4FD6] underline underline-offset-4">
              terms page
            </Link>
            .
          </p>
          <p>
            If you want anything on this page acted on, write to{" "}
            <a
              href={`mailto:${COMPANY.email}`}
              className="text-[#2C4FD6] underline underline-offset-4"
            >
              {COMPANY.email}
            </a>
            . A person reads it, and we aim to answer within a few days rather than the month the
            law allows.
          </p>
        </Section>

        <Section title="If you run a beach club">
          <p>
            Your listing holds business information: the name of the club, where it is, what it
            publishes as a price, its facilities, and the phone number, email or website the
            business puts on its own site. For most clubs this is company data rather than
            personal data, but for a sole trader the two overlap, so we treat it all with the same
            care.
          </p>
          <p>
            <strong className="text-[#1C2430]">Where it came from.</strong> Three places, and
            nowhere else. What your business publishes on its own website. The official Google
            Places API, for your rating, review count and photos, which is why those always carry
            Google's name. And our own research, written up by us rather than copied from anyone.
            We have never bought a list, and we do not scrape Instagram, TripAdvisor or Google
            Maps.
          </p>
          <p>
            <strong className="text-[#1C2430]">Why we hold it.</strong> To run a directory that
            helps travellers find you, which is our legitimate interest under Article 6(1)(f) and,
            we think, yours too: the listing is free, carries no advertising, and sends people to
            your own phone rather than through us. If you disagree, the next paragraph is the one
            that matters.
          </p>
          <p>
            <strong className="text-[#1C2430]">How to make it stop.</strong> One email and your
            listing comes down, usually the same day. No notice period and no contract, because
            there is nothing to cancel. If you would rather keep the page but never hear from us,
            the unsubscribe link in any of our emails does exactly that, permanently, and it
            survives us re-importing your club later.
          </p>
        </Section>

        <Section title="If you emailed a club through sunbedly">
          <p>
            When you send a club a request, the name and contact you type go to that club so they
            can answer you. We keep a copy so the club can see the request in their dashboard, and
            so we can tell whether the site works.
          </p>
          <p>
            We do not sell it, we do not market to it, and we never take payment for a bed, so
            there are no card details anywhere in this system. You pay the club, at the club.
          </p>
          <p>
            Want your request deleted? Email us with the club and the date and it goes, along with
            anything attached to it.
          </p>
        </Section>

        <Section title="If you left a review">
          <p>
            Your name and your review are published. The email address you gave is not stored: we
            keep only a one-way fingerprint of it, which is enough to stop the same person
            reviewing the same club twice and useless to anyone who reads the table. It cannot be
            turned back into your address, by us or by anyone else.
          </p>
          <p>
            Photographs you upload have to be your own, and should not show recognisable people who
            have not agreed to appear on a public website. Ask us and your review or your photo
            comes down.
          </p>
        </Section>

        <Section title="Cookies and analytics">
          <p>
            <strong className="text-[#1C2430]">Nothing loads until you say yes.</strong> We would
            like to use Google Analytics to see which coasts people look at. It sets a cookie, so
            we ask first: the Google script is not in this page at all until you accept, which is
            why you saw a bar at the bottom of the screen on your first visit. Refusing is one
            click, sits next to accepting and looks exactly the same, and changes nothing about how
            the site works for you.
          </p>
          <p>
            Changed your mind? <strong className="text-[#1C2430]">Cookie choice</strong> at the
            bottom of any page brings the bar back and deletes the Google cookies straight away. We
            do not use analytics to build a profile of you and we do not advertise anywhere.
          </p>
          <p>
            <strong className="text-[#1C2430]">What is stored without asking</strong>, because the
            site cannot work otherwise and none of it identifies you: your language choice, which
            lives in your own browser and is never sent to us; a session cookie holding the
            language your country suggests, which disappears when you close the browser; and, if
            you are us, a flag that keeps our own visits out of the statistics.
          </p>
          <p>
            Photos on club pages are served through us but come from Google, which means Google
            sees a request for the image. Their terms are why every one of those photos carries
            its photographer's name.
          </p>
        </Section>

        <Section title="How long we keep things">
          <p>
            Listings stay while the club exists or until someone asks us to remove one. Requests
            and reservations are kept for two seasons so a club can look back at last year, and a
            job runs on the first of every month that deletes everything older than that. It is
            not a promise we keep by hand. Analytics is kept for 14 months. An address that has
            unsubscribed is kept forever, on purpose: it is the only way to be certain we never
            write to it again.
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            You can ask what we hold, ask for it to be corrected, ask for it to be deleted, and
            object to us holding it at all. There is no form and no account: email us and say
            which. If we get it wrong you can complain to the Dutch DPA, the Autoriteit
            Persoonsgegevens, or to the regulator in your own country.
          </p>
        </Section>

        <Section title="Where the data sits">
          <p>
            On Cloudflare, which stores and serves it from inside the EU where it can. Google
            Analytics and the Google Places API are US companies operating under the EU-US Data
            Privacy Framework. Nothing else has a copy.
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
